<template>
    <div class="dashboard_classification">
        <div class="main_div">
            <ClassificationHeader
                ref="classificationHeader"
                title="Libro Mayor"
                description="Documentos del libro mayor (ERP) y su clasificación — solo lectura."
                :typeOptions="typeOptions"
                :periodDataSelect="erpRangeOptions"
                :localTypeFiles="[]"
                :selectedType="selectedType"
                :selectedRange="selectedRange"
                :searchFilter="searchFilter"
                :filterTableHasAccrual="filterTableHasAccrual"
                :allFilterCategories="allFilterCategories"
                :allAccountsOnDocs="allAccountsOnDocs"
                hide-unclassified-filter
                :hide-type-filter="isBalanceScope"
                @update:selectedType="onSelectedType"
                @update:selectedRange="onSelectedRange"
                @update:searchFilter="(v) => searchFilter = v"
                @update:filterTableHasAccrual="(v) => filterTableHasAccrual = v"
                @selected-categories="updateSelectedCategories"
                @selected-accounts="updateSelectedAccounts"
            >
                <!-- El mismo libro del reporte financiero que el resto de los
                    botones Exportar (decisiones §34); sin calendario propio,
                    corta al último mes con datos. -->
                <template #download>
                    <ReportExportButton to-last-data-month />
                </template>
                <!-- Sub-filtro EE.RR / Balance, centrado a la altura de los
                    selects del header y con label arriba, como Tipo Contraparte
                    y Rango de tiempo. Segmented control del dialecto ledger:
                    pista con lavado teal y thumb de papel que desliza entre
                    los dos estados (ver .scope_switch). -->
                <template #center-actions>
                    <div class="scope_filter">
                        <span id="scope_filter_label" class="scope_label">Estado financiero</span>
                        <div class="scope_switch"
                            role="group"
                            aria-labelledby="scope_filter_label"
                            :class="{ 'is-balance': accountScope === 'balance' }">
                            <span class="scope_thumb" aria-hidden="true"></span>
                            <button v-for="scope in accountScopeOptions" :key="scope.id"
                                type="button"
                                class="scope_btn"
                                :class="{ 'is-active': accountScope === scope.id }"
                                :aria-pressed="accountScope === scope.id ? 'true' : 'false'"
                                @click="setAccountScope(scope.id)">
                                {{ scope.label }}
                            </button>
                        </div>
                    </div>
                </template>
            </ClassificationHeader>

            <div class="underHeader">
                <CounterpartTable
                    ref="counterpartTable"
                    class="mb-5"
                    read-only
                    :show-owner="isConsolidatedCompany"
                    :entries="getPaginatedList"
                    :categories="categories"
                    :accountFilters="accountFilters"
                    :categoriesFilter="categoriesFilter"
                    :sortColumn="sortColumn"
                    :sortDirection="sortDirection"
                    :siiSyncing="false"
                    :loading="isLoading"
                    :maxHeight="tableMaxHeight"
                    :showReportLink="hasFinancialReport"
                    :hideCounterpartColumn="isBalanceScope"
                    :showBalanceConcept="isBalanceScope"
                    @sort="changeOrderGeneric"
                    @open="openLedgerDocsModal"
                    @open-report="openReport"
                    @clear-filter="clearFiltered"
                    @delete-filter="deleteItemFilter"
                />
                <Pagination ref="pagination" v-model="actualPage" :maxPages="maxPages" />
            </div>
        </div>
    </div>
</template>

<script>
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/900.css";

import Pagination from "@/components/generics/Pagination";
import CounterpartTable from "@/components/classification/CounterpartTable.vue";
import ClassificationHeader from "@/components/classification/ClassificationHeader.vue";
import ReportExportButton from "@/components/financialReports/ReportExportButton.vue";
import ClassifyAccountAttributes from "@/components/modals/ClassifyAccountAttributes";
import { TAB_IDS } from "@/views/financialReports/config/tabs";
import { getLedgerYears } from "@/api/carteraAnalista";
import { toIsoDay } from "@/utils/isoDay";
import { consoleHandlerError, notificate } from "@/utils/message";
import { mapActions, mapState } from "vuex";

// Igual que en DashboardClassificator: la tabla llena el alto libre del viewport
// y scrollea internamente (sin scroll de página). Cap medido en runtime.
const TABLE_MIN_HEIGHT = 220;

// Espera antes de mandar la búsqueda al servidor: filtrar dejó de ser local.
const SEARCH_DEBOUNCE_MS = 350;
const TABLE_BELOW_GAP = 48;

// Nivel 1 del plan de cuentas → tab del reporte financiero más aproximado. El
// ícono de gráfico de cada fila lleva ahí. Sin match → Estado de Resultados.
const LEVEL_1_TO_REPORT_TAB = {
    "Ventas": TAB_IDS.VENTAS,
    "Costos": TAB_IDS.COSTOS,
    "Gastos": TAB_IDS.GASTOS,
    "Activos": TAB_IDS.ACTIVOS,
    "No Operacional": TAB_IDS.NO_OPER,
};

// Tabs del reporte que tienen modal de detalle de contraparte (Ventas/Costos/
// Gastos). Sólo en estos dejamos un pendingDrill para auto-abrir la contraparte.
const REPORT_TABS_WITH_COUNTERPART_DETAIL = [
    TAB_IDS.VENTAS, TAB_IDS.COSTOS, TAB_IDS.GASTOS,
];

// Sub-filtro EE.RR / Balance del mayor (param `account_scope` del backend):
// "eerr" lista contrapartes SIN cuentas de balance (las mixtas pertenecen a
// Balance); "balance" deja sólo los docs de cuentas de balance, con totales
// acotados a ellos. Mismo criterio dentro del modal de documentos.
const ACCOUNT_SCOPE_OPTIONS = [
    { id: "eerr", label: "Resultados" },
    { id: "balance", label: "Balance" },
];

// Vista de Libro Mayor (ERP), SOLO LECTURA. Reusa el módulo `classification` y
// los componentes del clasificador en modo readOnly, pero pide al backend sólo
// los documentos del mayor (`ledger_only: true`) y no permite editar: sin upload,
// sin deep-link, sin árbol editable (el detalle por documento abre un visor
// read-only). Es mutuamente excluyente con el clasificador (ambos `v-if` en
// Dashboard.vue), así que refetchea al montar y no coexisten.
export default {
    name: "DashboardErp",
    components: {
        Pagination,
        CounterpartTable,
        ClassificationHeader,
        ReportExportButton,
    },
    data() {
        return {
            countDirectionOrder: 0,
            sortColumn: "order",
            sortDirection: "asc",
            modalDefault: {
                parent: this,
                width: "99%",
                hasModalCard: true,
                canCancel: ["escape", "outside"],
                customClass: "center-always-modal",
            },
            searchFilter: "",
            filterTableHasAccrual: "",
            actualPage: 1,
            itemsPerPage: 20,
            // Total de contrapartes del servidor: manda en el paginador.
            totalCounterparts: 0,
            searchTimer: null,
            allAccountsOnDocs: [],
            allFilterCategories: [],
            accountFilters: [],
            categoriesFilter: [],
            tableMaxHeight: null,
            // Años con data del mayor (backend `ledger_years`): alimentan el
            // filtro "Rango de tiempo" PROPIO de esta vista (un item por año).
            ledgerYears: [],
            // Sub-filtro activo (EE.RR por defecto). El backend filtra por él,
            // así que cada cambio refetchea el listado.
            accountScope: "eerr",
            accountScopeOptions: ACCOUNT_SCOPE_OPTIONS,
            // Comparte el módulo Vuex `classification` con DashboardClassificator.
            // Si esta vista se destruye (navegación al clasificador), su cadena
            // async no debe disparar un fetch tardío que pise el otro
            // listado. Ver guard en getAllPaginatedCounterparts.
            isActive: true,
        };
    },
    mounted() {
        // Comparte el slot de contrapartes con el clasificador: limpiar al montar
        // evita mostrar las del clasificador hasta que llega el fetch del mayor.
        this.clearCounterparts();
        this.initClassificationFilters();
        this.getAllAccounts().then(async () => {
            // El rango de esta vista son los años con data del mayor: se cargan
            // ANTES de elegir el default (el año más reciente) y fetchear.
            await this.loadLedgerYears();
            this.onSelectedRange(this.erpRangeOptions[0].value);
        });

        this.changeColumn("total_3_months");
        this.sortData("total_3_months");
        this.changeColumn("total_3_months");
        this.sortData("total_3_months");

        this.$nextTick(this.measureTableHeight);
        window.addEventListener("resize", this.measureTableHeight);
        if (typeof ResizeObserver !== "undefined") {
            this.headerResizeObserver = new ResizeObserver(() => this.measureTableHeight());
            const headerEl = this.$refs.classificationHeader && this.$refs.classificationHeader.$el;
            if (headerEl) this.headerResizeObserver.observe(headerEl);
        }
    },
    beforeDestroy() {
        this.isActive = false;
        clearTimeout(this.searchTimer);
        window.removeEventListener("resize", this.measureTableHeight);
        if (this.headerResizeObserver) {
            this.headerResizeObserver.disconnect();
            this.headerResizeObserver = null;
        }
    },
    methods: {
        ...mapActions("classification", ["getCategories", "getAllAccounts",
            "getLedgerCounterpartsPage", "fetchLedgerCounterpartFacets",
            "setSelectedPeriodCounterparts", "updateSelectedType", "initClassificationFilters",
            "clearCounterparts"]),
        measureTableHeight() {
            const tableEl = this.$refs.counterpartTable && this.$refs.counterpartTable.$el;
            if (!tableEl) return;
            const top = tableEl.getBoundingClientRect().top;
            const paginationEl = this.$refs.pagination && this.$refs.pagination.$el;
            const paginationH = paginationEl ? paginationEl.offsetHeight : 0;
            const available = window.innerHeight - top - paginationH - TABLE_BELOW_GAP;
            this.tableMaxHeight = Math.max(TABLE_MIN_HEIGHT, Math.round(available));
        },
        updateSelectedAccounts(accountsSelected) {
            this.accountFilters = [...accountsSelected];
            this.allFiltersToEntries();
        },
        updateSelectedCategories(categoriesSelected) {
            this.categoriesFilter = [...categoriesSelected];
            this.allFiltersToEntries();
        },
        clearFiltered(type) {
            if (type == "accounts") {
                this.$refs.classificationHeader.clearAccountFilter();
            } else {
                this.$refs.classificationHeader.clearCategoryFilter();
            }
            this.allFiltersToEntries();
        },
        deleteItemFilter(type, item) {
            if (type == "accounts") {
                this.$refs.classificationHeader.deleteAccountItem(item);
            } else {
                this.$refs.classificationHeader.deleteCategoryItem(item);
            }
        },
        onSelectedType(newValue) {
            const optionSelected = this.typeOptions.find((o) => o.value == newValue);
            this.updateSelectedType(optionSelected);
            this.allFiltersToEntries();
        },
        // Carga los años con data del mayor. Si falla, `erpRangeOptions` cae al
        // fallback "Toda la historia" y la vista sigue operativa.
        async loadLedgerYears() {
            try {
                const { data } = await getLedgerYears();
                this.ledgerYears = data.years || [];
            } catch (error) {
                console.error("Error loading ledger years:", error);
                this.ledgerYears = [];
            }
        },
        onSelectedRange(newValue) {
            const optionSelected = this.erpRangeOptions.find((o) => o.value == newValue);
            this.setSelectedPeriodCounterparts(optionSelected);
            this.actualPage = 1;
            this.loadFacets();
            this.getAllPaginatedCounterparts();
        },
        setAccountScope(scope) {
            if (scope === this.accountScope) return;
            this.accountScope = scope;
            this.actualPage = 1;
            // En Balance la contraparte no aplica: el select Tipo Contraparte y
            // la columna se ocultan, así que se neutraliza cualquier filtro u
            // orden por contraparte que quedara activo (filtraría invisible).
            if (scope === "balance") {
                if (this.selectedType !== "todos") this.onSelectedType("todos");
                if (this.sortColumn === "counterpart_type") {
                    this.countDirectionOrder = 0;
                    this.sortColumn = "order";
                    this.sortDirection = "asc";
                }
            }
            this.loadFacets();
            this.getAllPaginatedCounterparts();
        },
        resetMsgs() {
            this.sortColumn = "total_3_months";
            this.sortDirection = "desc";
        },
        sortData(column, subtype = null, reset_msg = true) {
            if (reset_msg) {
                this.resetMsgs();
            }
            let col_name = column;
            if (subtype != null) {
                col_name = "attribute_" + subtype;
            }
            if (this.countDirectionOrder == 0) {
                this.sortColumn = "order";
                this.sortDirection = "asc";
            } else if (this.countDirectionOrder == 1) {
                this.sortColumn = col_name;
                this.sortDirection = "asc";
            } else if (this.countDirectionOrder == 2) {
                this.sortColumn = col_name;
                this.sortDirection = "desc";
            }
        },
        changeColumn(column, subtype = null) {
            let column_name = column;
            if (subtype) {
                column_name = column + "_" + subtype;
            }
            if (this.sortColumn != column_name) {
                this.countDirectionOrder = 0;
                this.sortColumn = column;
                if (subtype) {
                    this.sortColumn = column + "_" + subtype;
                }
            }
            this.countDirectionOrder = (this.countDirectionOrder + 1) % 3;
        },
        changeOrderGeneric(column, subtype = null) {
            this.changeColumn(column, subtype);
            this.sortData(column, subtype, false);
            // El orden es del servidor: se pide la primera página con el nuevo
            // criterio en vez de reordenar las 20 filas que hay en pantalla.
            this.actualPage = 1;
            this.getAllPaginatedCounterparts();
        },
        // Una PÁGINA, ordenada y filtrada por el servidor. Antes pedía
        // `to: 999999` y resolvía todo en JS: con el mayor de Maryun eran
        // 257.963 filas y 156 MB que no llegaban a pintarse.
        async getAllPaginatedCounterparts() {
            // Si la vista ya se destruyó (navegación al clasificador), no dispares el
            // fetch: pisaría las contrapartes del clasificador en el store compartido.
            if (!this.isActive) return;
            const { total, error, stale } = await this.getLedgerCounterpartsPage({
                ...this.serverFilters(),
                page: this.actualPage,
                page_size: this.itemsPerPage,
                order_by: this.serverOrderColumn,
                direction: this.sortDirection,
            });
            // Request reemplazado por uno más nuevo: no toca nada (el total
            // viejo pisaría el paginador con un número que ya no corresponde).
            if (stale) return;
            this.totalCounterparts = total;
            // Consulta cancelada por timeout u otro fallo: el analista tiene
            // que leer qué pasó en vez de quedarse mirando una tabla vacía.
            if (error) {
                notificate(`<strong>No se pudo cargar la lista</strong><br>${error}`,
                    "is-warning is-light");
            }
        },
        // Los filtros que viajan al servidor, iguales para la página y para
        // los combos: los dos tienen que ver el mismo conjunto de contrapartes.
        serverFilters() {
            return {
                from_day: toIsoDay(this.selectedPeriodCounterparts.from),
                to_day: toIsoDay(this.selectedPeriodCounterparts.to),
                account_scope: this.accountScope,
                search: this.searchFilter || "",
                counterpart_type: this.selectedType === "todos" ? "" : this.selectedType,
                accounts: this.accountFilters,
                categories: this.categoriesFilter,
                has_accrual: this.filterTableHasAccrual === "Devengados" ? true : null,
            };
        },
        // Los combos de cuentas y categorías: los sirve el backend porque el
        // front ya no tiene el dataset entero para derivarlos. Se piden con
        // los filtros activos, así ofrecen sólo lo que hay en la lista
        // visible (como cuando se derivaban de las filas filtradas en JS).
        async loadFacets() {
            if (!this.isActive) return;
            try {
                const facets = await this.fetchLedgerCounterpartFacets(this.serverFilters());
                // Request reemplazado por uno más nuevo: no toca los combos.
                if (!facets) return;
                // El filtro viaja por nombre (level_3). En una empresa
                // consolidada el plan es la unión de los miembros y el mismo
                // nombre llega con varios ids: se ofrece una sola opción.
                const seenNames = new Set();
                this.allAccountsOnDocs = this.allAccounts.filter((account) => {
                    if (!facets.accounts.includes(account.level_3)) return false;
                    if (seenNames.has(account.level_3)) return false;
                    seenNames.add(account.level_3);
                    return true;
                });
                this.allFilterCategories = facets.categories;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[erp]", silent: true });
            }
        },
        openLedgerDocsModal(contraparte) {
            // Mismo modal "drawer" del clasificador (ClassifyAccountAttributes)
            // pero en read-only y pidiendo sólo el mayor: oculta el árbol de
            // prorrateo/clasificación y muestra la tabla de documentos (con su
            // devengo) idéntica a la del clasificador.
            this.$buefy.modal.open({
                ...this.modalDefault,
                component: ClassifyAccountAttributes,
                customClass: "classify_modal_width",
                props: {
                    rangeOptions: this.erpRangeOptions,
                    rangeFromMainScreen: this.selectedRange,
                    types: this.categories,
                    counterpart: contraparte,
                    readOnly: true,
                    ledgerOnly: true,
                    // El modal filtra sus documentos con el mismo sub-filtro
                    // EE.RR/Balance activo en la lista.
                    accountScope: this.accountScope,
                },
            });
        },
        // Nivel 1 de la primera cuenta madre (level_2) de la contraparte. El plan
        // de cuentas (allAccounts) trae level_1/2/3; resolvemos por level_2.
        reportTabForEntry(entry) {
            const motherAccounts = entry.calculatedMotherAccounts || [];
            for (const level2 of motherAccounts) {
                const account = this.allAccounts.find((a) => a.level_2 == level2);
                if (account && LEVEL_1_TO_REPORT_TAB[account.level_1]) {
                    return LEVEL_1_TO_REPORT_TAB[account.level_1];
                }
            }
            return TAB_IDS.RESULTADOS;
        },
        openReport(entry) {
            // Lleva al tab del reporte financiero más aproximado a la cuenta de la
            // contraparte. El cambio de `section` lo escucha Dashboard.vue (watcher
            // de $route.query.section) y `tab` lo lee DashboardReports.
            const tab = this.reportTabForEntry(entry);
            // Si el tab destino tiene detalle de contraparte, dejamos un pendingDrill
            // (mismo mecanismo cross-tab del P&L) para que al montar abra el modal de
            // ESTA contraparte (resuelto por alias). En otros tabs sólo navegamos.
            this.$store.commit(
                "reportsFilters/SET_PENDING_DRILL",
                REPORT_TABS_WITH_COUNTERPART_DETAIL.includes(tab)
                    ? { tab, counterpartAlias: entry.alias, counterpartType: entry.counterpart_type }
                    : null,
            );
            this.$router
                .push({ query: { ...this.$route.query, section: "reports", tab } })
                .catch((err) => {
                    if (err.name !== "NavigationDuplicated") throw err;
                });
        },
        // Cambiar un filtro vuelve a pedir la primera página: filtrar es del
        // servidor desde que la lista se pagina.
        // Cambió un filtro: primera página y combos, en paralelo. Cambiar de
        // página no pasa por acá, así los combos no se recalculan al paginar.
        allFiltersToEntries() {
            this.actualPage = 1;
            this.loadFacets();
            return this.getAllPaginatedCounterparts();
        },
    },
    computed: {
        ...mapState("classification", ["isLoading", "selectedPeriodCounterparts",
            "categories", "allAccounts", "allOrderedCounterparts", "selectedType",
            "selectedRange", "typeOptions"]),
        ...mapState("userAuth", ["user", "company"]),
        // Vista acumulada: las filas del mayor muestran su empresa de origen.
        isConsolidatedCompany() {
            return Boolean(this.company && this.company.is_consolidated);
        },
        // El ícono de gráfico (link al reporte) sólo se muestra si el usuario
        // tiene acceso al reporte financiero.
        hasFinancialReport: function () {
            return !!(this.user && this.user.has_financial_report);
        },
        // En alcance Balance las contrapartes no aplican: se ocultan el select
        // Tipo Contraparte del header y la columna Contraparte de la tabla.
        isBalanceScope: function () {
            return this.accountScope === "balance";
        },
        // Opciones del "Rango de tiempo" PROPIAS de esta vista: un item por año
        // con data del mayor (descendente: el más reciente primero, y es el
        // default al montar). El clasificador NO cambia — sigue usando el
        // periodDataSelect global del store. Sin años (mayor vacío o fetch
        // fallido) cae a "Toda la historia" para que la vista siga operativa.
        erpRangeOptions: function () {
            if (!this.ledgerYears.length) {
                return [{
                    value: "todo",
                    label: "Toda la historia",
                    from: new Date(2015, 0, 1),
                    to: new Date(),
                }];
            }
            return [...this.ledgerYears]
                .sort((a, b) => b - a)
                .map((year) => ({
                    value: String(year),
                    label: String(year),
                    from: new Date(year, 0, 1),
                    to: new Date(year, 11, 31),
                }));
        },
        // La lista que llega YA es la página: ni filtro ni corte en el cliente.
        filteredListCounterparts: function () {
            return this.allOrderedCounterparts;
        },
        maxPages: function () {
            return Math.ceil(this.totalCounterparts / this.itemsPerPage) || 1;
        },
        getPaginatedList: function () {
            return this.allOrderedCounterparts;
        },
        // Nombre de la columna que entiende el servidor: 'order' es "sin orden
        // elegido" y los atributos viajan como `attribute` (el tipo ya va en
        // el filtro), no como `attribute_<tipo>`.
        serverOrderColumn: function () {
            if (!this.sortColumn || this.sortColumn === "order") return "total_3_months";
            return this.sortColumn.startsWith("attribute_") ? "attribute" : this.sortColumn;
        },
    },
    watch: {
        // La búsqueda ahora viaja al servidor: se espera a que el analista
        // deje de tipear para no disparar una query por tecla.
        searchFilter() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.allFiltersToEntries(), SEARCH_DEBOUNCE_MS);
        },
        filterTableHasAccrual() {
            this.allFiltersToEntries();
        },
        actualPage() {
            this.getAllPaginatedCounterparts();
        },
    },
};
</script>

<style lang="scss" scoped>
.dashboard_classification {
    // Mismo dialecto ledger que DashboardClassificator (paper/rule/ink/accent).
    // Los hijos (ClassificationHeader, CounterpartTable) heredan estas vars.
    --paper: #ffffff;
    --paper-soft: #fafbfc;
    --rule: rgba(16, 24, 40, 0.08);
    --rule-strong: rgba(16, 24, 40, 0.14);
    --ink: #1d2939;
    --ink-muted: #475467;
    --ink-faint: #98a2b3;
    --accent: #0d5154;
    --accent-wash: rgba(13, 81, 84, 0.06);
    // Acento del sub-filtro EE.RR/Balance: el MISMO teal vivo de marca que
    // los filtros de Ventas/KPIs del reporte (--report-filter-accent,
    // #00a9ad) — un solo color de "filtro activo" en toda la app. hover = un
    // paso más oscuro de la familia; glow = sombra tintada de la ficha (la
    // eleva y la hace leerse presionable).
    --scope-accent: #00a9ad;
    --scope-accent-hover: #008f93;
    --scope-accent-glow: rgba(0, 169, 173, 0.35);
    --hover: rgba(16, 24, 40, 0.03);
    --radius: 6px;

    background-color: var(--paper-soft);
    font-family: Inter, "Open Sans", sans-serif;
}

.underHeader {
    position: relative;
}

// El contenedor se encoge al ancho del switch para que el label
// se centre respecto a los botones EE.RR/Balance, no a la celda.
.scope_filter {
    width: fit-content;
}

// Label sobre el switch, mismo formato que los de los selects vecinos
// (SelectComponent .custom_label: 0.9rem + 0.5rem de separación); el color
// sale del dialecto local de la vista.
.scope_label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    text-align: center;
    color: var(--ink-muted);
}

// Sub-filtro EE.RR/Balance del header. Segmented control PRESIONABLE: la
// pista es un pozo (sombra interior) y la ficha activa —teal de marca
// (--scope-accent)— queda ELEVADA sobre él con sombra tintada, como una
// tecla física; presionar la otra opción la hunde un pelo (:active) y la
// ficha DESLIZA hacia allí — el color no se swapea, la ficha se mueve.
// Labels en mayúsculas con tracking, como los encabezados de columna de un
// registro contable. Va scoped en la vista porque se inyecta por slot (los
// estilos scoped de ClassificationHeader no alcanzan al markup del slot).
.scope_switch {
    position: relative;
    display: grid;
    // Celdas iguales: el thumb mide exactamente 50% y calza en ambas.
    grid-template-columns: 1fr 1fr;
    padding: 3px;
    // Misma altura que los controles vecinos del header (SelectComponent
    // --height y .btnNoClassify: 46px); los botones se estiran a la celda.
    box-sizing: border-box;
    height: 46px;
    background: var(--paper);
    border: 1px solid var(--rule-strong);
    border-radius: 8px;
    width: fit-content;
    // Pozo: la pista se hunde para que la ficha se lea en relieve.
    box-shadow: inset 0 1px 3px rgba(16, 24, 40, 0.08);
}

.scope_thumb {
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: 3px;
    width: calc(50% - 3px);
    background: var(--scope-accent);
    border-radius: 5px;
    // Elevación en dos capas: contacto + glow tintado del acento.
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.25), 0 2px 6px var(--scope-accent-glow);
    transition: transform 0.22s cubic-bezier(0.2, 0, 0, 1);
}

.scope_switch.is-balance .scope_thumb {
    transform: translateX(100%);
}

.scope_btn {
    position: relative;
    z-index: 1;
    // Sin padding vertical: la altura la fija el track de 46px del switch.
    padding: 0 24px;
    border: none;
    background: transparent;
    border-radius: 5px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
    color: var(--ink-muted);
    cursor: pointer;
    transition: color 0.15s ease, transform 0.1s ease;

    &:hover {
        color: var(--scope-accent-hover);
    }

    // Feedback físico: la opción presionada se hunde un pelo hacia el pozo.
    &:active {
        transform: translateY(1px);
    }

    &.is-active {
        color: var(--paper);

        // El hover del activo oscurece la ficha un paso (misma familia).
        // La ficha es un hermano previo: :has() sube el estado. Sin soporte
        // de :has(), el hover simplemente no oscurece — inofensivo.
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--paper), 0 0 0 4px var(--scope-accent);
    }
}

.scope_switch:has(.scope_btn.is-active:hover) .scope_thumb {
    background: var(--scope-accent-hover);
}

// En pantallas chicas el control baja a su propia fila (grid del header);
// menos padding horizontal para que respire junto a la tabla (la altura
// sigue clavada en los 46px del track).
@media (max-width: 1280px) {
    .scope_btn {
        padding: 0 20px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .scope_thumb {
        transition: none;
    }

    .scope_btn {
        transition: none;
    }
}

.main_div {
    padding-left: clamp(16px, 2vw, 32px);
    padding-right: clamp(16px, 5vw, 90px);
}
</style>
